/**
 * Regras de perfil que decidem, antes da chamada, se a Home precisa ler o dado de uma seção.
 * Quem não vê a seção não paga a leitura (DCE-16, DCE-17, DCE-19, DCE-23).
 */

/** Os campos do `userInfo` que as regras leem. */
export type PlayerProfile = {
  chave?: string;
  is_manager?: boolean;
  cad_missao?: number | null;
  ecoa?: string | false | null;
  visualizador?: boolean;
  ro?: string | null;
  cod_funcao?: number | null;
  cod_etapa?: number | null;
  /** Parceiros de dupla; a base antiga gravava texto, que não entra na regra. */
  mad_dupla?: { cod_etapa?: number | null; ro?: string | null }[] | string | null;
};

/** Os flags de `user.extra` que as regras leem. */
type PlayerFlags = { extra?: { rede?: boolean; recognition?: boolean; actions?: boolean } | null };

const ECOA_ROLES = ['TUTOR', 'COORDENADOR', 'INTEGRANTE'];

/** Mesma regra do `canRegister` de `task-cycles.tsx`: tem cadastro de missão e não é gestor. */
export function canRegisterRealizacao(userInfo?: PlayerProfile | null): boolean {
  return !!userInfo && typeof userInfo.cad_missao === 'number' && userInfo.cad_missao > 0 && !userInfo.is_manager;
}

/** Etapa da escada de apreciação: Validação (1) a Nível 3 (5). */
function isLadderStep(step: unknown): boolean {
  return typeof step === 'number' && step > 0 && step <= 5;
}

/** Aprecia Realizações Destaque: está na escada ou é parceiro de dupla de quem está (FECH-03). */
export function isLadderManager(userInfo?: PlayerProfile | null): boolean {
  const partners = userInfo?.mad_dupla;

  return (
    isLadderStep(userInfo?.cod_etapa) || (Array.isArray(partners) && partners.some(partner => isLadderStep(partner?.cod_etapa)))
  );
}

const TEAM_RO = /(3GUE|2GUT|2GCA|2GUA|2GUE|1GUT|1GUA|1GCA)/i;
const TEAM_FUNCTIONS = [3002, 3003, 4005, 4007];

/** `ro` de gestor de equipe. */
export function isTeamRo(ro?: string | null): boolean {
  return TEAM_RO.test(ro ?? '');
}

/** Autoriza participações em Realização Destaque: gestor de equipe, pelo `ro` ou pela função (FECH-07). */
export function canApproveParticipants(userInfo?: PlayerProfile | null): boolean {
  const roleCode = userInfo?.cod_funcao;

  return isTeamRo(userInfo?.ro) || (typeof roleCode === 'number' && TEAM_FUNCTIONS.includes(roleCode));
}

type RecognizeConditions = {
  cycleIsOpen: boolean;
  /** `user.extra.recognition !== false`. */
  recognitionEnabled: boolean;
  /** `userInfo.visualizador`: só lê, não envia. */
  visualizador: boolean;
};

/** As mesmas condições que habilitam o painel Reconhecer. */
export function canRecognizeNow({ cycleIsOpen, recognitionEnabled, visualizador }: RecognizeConditions): boolean {
  return cycleIsOpen && recognitionEnabled && !visualizador;
}

/** Papel ECOA que mostra a seção TaskCyclesEcoa; a rede nunca a vê. */
export function hasEcoaRole(userInfo: PlayerProfile | null | undefined, isRede: boolean): boolean {
  return !isRede && !!userInfo?.ecoa && ECOA_ROLES.includes(userInfo.ecoa);
}

type HomeReadsInput = {
  user?: PlayerFlags | null;
  userInfo?: PlayerProfile | null;
  /** O ciclo selecionado está aberto e há ciclo corrente. */
  cycleIsOpen: boolean;
};

export type HomeReadsPlan = {
  /** Lê o rascunho de realização (`get_partial_task`), DCE-16. */
  canRegister: boolean;
  /** Lê o rascunho de reconhecimento (`get_partial_recognition`), DCE-17. */
  canRecognize: boolean;
  /** Monta TaskCyclesEcoa (`entregas_ecoa`, `tasks_manager_ecoa`), DCE-23. */
  showEcoa: boolean;
  /** Monta Ações de Engajamento (`GET challenge`, `find/challenge`, `get_user_equipe_type`), DCE-19. */
  showEngagement: boolean;
};

/** Única fonte das decisões de leitura da Home: quais seções montam e quais rascunhos são lidos. */
export function homeReadsPlan({ user, userInfo, cycleIsOpen }: HomeReadsInput): HomeReadsPlan {
  const extra = user?.extra;

  return {
    canRegister: canRegisterRealizacao(userInfo),
    canRecognize: canRecognizeNow({
      cycleIsOpen,
      recognitionEnabled: extra?.recognition !== false,
      visualizador: !!userInfo?.visualizador,
    }),
    showEcoa: hasEcoaRole(userInfo, !!extra?.rede),
    showEngagement: extra?.actions !== false,
  };
}
